import PropTypes from "prop-types";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { IconChevronDown, IconMail, IconMapPin, IconPhone } from "@tabler/icons";

// A preview of a website section with what is typed in the editor, before
// it is saved: the words in place, in the website's light card, so a typo
// or a wrong number is seen before customers see it. It is drawn here, not
// loaded from the website, so it is close to the page, not a copy of it.

/** The frame: what the preview is of, and that nothing is saved yet. */
export function SitePreview({ where, children }) {
  return (
    // flexShrink 0: in a dialog's column a clipped box would otherwise be
    // squeezed shorter than its words.
    <Box component="section" aria-label={`Preview: ${where}`} sx={{ borderRadius: "16px", border: 1, borderColor: "divider", overflow: "hidden", flexShrink: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1, bgcolor: "action.hover", flexWrap: "wrap" }} useFlexGap>
        <Chip size="small" label="Preview" color="primary" variant="outlined" />
        <Typography variant="body2" color="text.secondary">
          {where}. Not saved yet: this is how it will read.
        </Typography>
      </Stack>
      {/* The website is light whatever the CMS theme. */}
      <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "#FFFFFF", color: "#14112B" }}>{children}</Box>
    </Box>
  );
}

SitePreview.propTypes = { where: PropTypes.string.isRequired, children: PropTypes.node };

/** One question of the website's "What people ask", opened. */
export function FaqPreview({ question, answer }) {
  return (
    <Box sx={{ borderRadius: "14px", border: "1px solid #E4E2F0", p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Typography sx={{ fontWeight: 700, color: "inherit", overflowWrap: "anywhere" }}>{question || "Your question"}</Typography>
        <Box sx={{ transform: "rotate(180deg)", display: "flex", color: "#5B5876" }} aria-hidden>
          <IconChevronDown size={18} />
        </Box>
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.75, color: "#3D3A57", whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
        {answer || "Your answer"}
      </Typography>
    </Box>
  );
}

FaqPreview.propTypes = { question: PropTypes.string, answer: PropTypes.string };

/** The contact lines as the website's footer shows them. */
export function ContactPreview({ email, phone, location }) {
  const line = (Icon, value, empty) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: value ? "#3D3A57" : "#8B88A3" }}>
      <Icon size={16} aria-hidden />
      <Typography variant="body2" sx={{ color: "inherit", overflowWrap: "anywhere" }}>
        {value || empty}
      </Typography>
    </Stack>
  );
  return (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "inherit" }}>
        Contact
      </Typography>
      {line(IconMail, email, "No email")}
      {line(IconPhone, phone, "No phone")}
      {line(IconMapPin, location, "No address")}
    </Stack>
  );
}

ContactPreview.propTypes = { email: PropTypes.string, phone: PropTypes.string, location: PropTypes.string };

/** The About page's "Supported by" row: each logo in its white card. */
export function LogosPreview({ logos, renderLogo }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "inherit", mb: 1 }}>
        Supported by
      </Typography>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ overflowX: "auto", pb: 0.5 }}>
        {logos.map((logo, index) => (
          <Box key={logo.key ?? index} sx={{ flexShrink: 0, outline: logo.isNew ? "2px solid #2E7D32" : "none", borderRadius: "20px" }}>
            {renderLogo(logo.src)}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

LogosPreview.propTypes = { logos: PropTypes.array.isRequired, renderLogo: PropTypes.func.isRequired };
